'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  const navItems = [
    { label: 'How It Works', section: 'pipeline' },
    { label: 'Features', section: 'features' },
    { label: 'Pricing', section: 'pricing' },
  ];

  const signInHref = process.env.NEXT_PUBLIC_APP_URL || 'https://app.pipeout.app';
  const requestAccessHref = pathname === '/' ? '#request-access' : '/#request-access';

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const getSectionHref = useCallback(
    (section: string) => {
      if (pathname === '/' || (pathname === '/pricing' && section === 'pricing')) {
        return `#${section}`;
      }
      return `/#${section}`;
    },
    [pathname]
  );

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mobileOpen, closeMobile]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[90] bg-bg-primary/86 backdrop-blur-md border-b border-white/6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center space-x-2">
            <img src="/pipeout_wht.png" alt="Pipeout" width={24} height={24} className="h-6 w-6" />
            <span className="text-xl font-bold text-white">Pipeout</span>
          </Link>

          <nav className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.label}
                href={getSectionHref(item.section)}
                className="text-base text-text-secondary hover:text-white transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-accent-blue after:transition-all hover:after:w-full"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <a
              href={signInHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-text-secondary hover:text-white transition-colors"
            >
              Sign In
            </a>
            <a href={requestAccessHref} className="btn-primary rounded-[20px] px-6 py-2.5 text-sm font-semibold">
              Request Access
            </a>
          </div>

          <button
            type="button"
            className="md:hidden inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-[#0f1218] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] hover:border-white/16 hover:bg-[#141922] transition-colors"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X className="h-6 w-6 text-white" strokeWidth={2.25} /> : <Menu className="h-6 w-6 text-white" strokeWidth={2.25} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMobile}
            className="fixed left-0 right-0 bottom-0 top-16 z-[70] bg-black/72 backdrop-blur-sm md:hidden"
          />

          <div
            id="mobile-menu"
            className="fixed left-4 right-4 top-[4.5rem] z-[80] rounded-[1.75rem] border border-white/6 bg-[#090b10] px-4 pb-4 pt-4 shadow-[0_28px_48px_-24px_rgba(0,0,0,0.9)] md:hidden"
          >
            <a
              href={requestAccessHref}
              onClick={closeMobile}
              className="btn-primary block w-full rounded-[20px] px-7 py-3 text-center text-sm font-semibold"
            >
              Request Access
            </a>

            <nav className="mt-4 flex flex-col space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  href={getSectionHref(item.section)}
                  onClick={closeMobile}
                  className="rounded-xl px-3 py-2.5 text-base text-white/88 hover:text-white hover:bg-white/[0.05] transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <a
              href={signInHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobile}
              className="mt-2 block w-full rounded-full border border-white/10 px-7 py-2.5 text-center text-sm font-semibold text-white/88 hover:text-white hover:border-white/16 hover:bg-white/[0.05] transition-colors"
            >
              Sign In
            </a>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
